import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function PaymentInLieuOfNotice() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" data-testid="home-link" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <div className="h-32 md:h-48" style={{ background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.background} 50%, ${colors.backgroundLight} 100%)` }} />

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 data-testid="page-title" className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            What Does Payment in Lieu of Notice Mean?
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                Payment in lieu of notice (PILON) means the employer pays you for your notice period instead of requiring you to work it.
              </p>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Example Clause</h2>
              <div className="p-6 rounded-xl text-base" style={{ backgroundColor: colors.backgroundDark, color: colors.text, fontFamily: 'monospace' }}>
                "The Company reserves the right to terminate this agreement immediately by providing payment in lieu of notice. Such payment shall be calculated based on Employee's base salary for the notice period, and Employee's obligations under this agreement shall cease immediately upon receipt of payment."
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                Instead of working through your notice period, you receive equivalent pay and leave immediately. This arrangement:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Allows immediate departure from the workplace
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Usually covers base salary only (may exclude bonuses or benefits)
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Is typically the employer's choice, not the employee's
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Can be used when the employer wants clean separation
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Notable Wording</h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"Reserves the right"</strong> - employer decides whether to use this option
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"Base salary"</strong> - may not include commissions, bonuses, or variable pay
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"Immediately upon receipt"</strong> - employment ends when payment is made
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Common Purpose</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Employers use PILON to remove access to systems, clients, or sensitive information immediately. It's common when there are concerns about data security, client relationships, or workplace disruption. It also allows both parties to move on quickly without the awkwardness of working through notice.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Typical Effects</h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  You receive pay but don't work the notice period
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Access to workplace, email, and systems ends immediately
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  May affect benefits that accrue during employment
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Allows you to start a new job sooner
                </li>
              </ul>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Tax treatment of PILON varies by jurisdiction. Some contracts specify whether PILON is permitted or requires employee consent. The calculation method should be clear—whether it includes only salary or also benefits. Outstanding holiday pay is typically separate from PILON. This explanation covers typical contract language but doesn't constitute legal advice about your specific situation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/explain-notice-period-clause" data-testid="related-link-notice" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Notice Period</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Learn about standard notice requirements</p>
                </Link>
                <Link to="/explain-termination-for-cause-clause" data-testid="related-link-termination" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Termination for Cause</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Understand immediate dismissal</p>
                </Link>
              </div>
            </section>

            <div data-testid="cta-section" className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
              <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
              <Link to="/" data-testid="cta-button" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
                Try ClauseClear
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Sponsorship Placeholder */}
            <SponsorPlaceholder variant="full" />
          </div>
        </div>
      </article>

      <footer className="px-6 md:px-12 lg:px-24 py-12 text-center " style={{ borderColor: colors.border }}>
        <p className="text-sm mb-2" style={{ color: colors.textMuted }}>ClauseClear is an educational tool. It does not provide legal advice.</p>
        <a href="https://buymeacoffee.com/jamesmoneyz" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          Support the project
        </a>
      </footer>
    </div>
  );
}
